import { useCallback, useEffect, useState } from 'react'
import { nanoid } from 'nanoid'
import {
  createForm,
  Flex,
  Notice,
  Select,
  Text,
  TextInput,
} from '@applyboard/crystal-ui'
import { set } from 'lodash'
import {
  FileData,
  HasLanguageProficiencyCodes,
  LanguageProficiencyData,
  LanguageProficiencyInformation,
} from '@applyboard/school-applications-types-lib'
import { Loading } from '../../Loading'

import { RawApplicationResponse, getLanguageProficiencyFile, useUpdateApplication } from '../../../hooks'

import { ApplicationFormCard } from './ApplicationFormCard'
import { FileUploadField, FileUploadFieldValue } from './FileUploadField'
import { ChangeLanguageTestDialog } from '../ChangeLanguageTestDialog'

import { DocumentTags, LanguageExamAvailability, TimelineTabs } from '../../../utils/enums'
import { getLanguageProficiencyFieldsInfo } from '../../../utils'
import { convertObjToListWithSpecificLastItem, getFilesOfTypeLanguageProficiency, hasNewLanguageProficiencySchema } from './utils'
import { LanguageProficiencyTypeOption, useGetLanguageProficiencyTypes } from '../../../hooks/useGetLanguageProficiencyTypes'
import { StudentApplication } from '../types'
import { Asterisk } from '../../Asterisk'
import { useGetTimelineItems } from '../../../hooks/useGetTimelineItems'
import { useCreateLanguageProficiency } from '../../../hooks/useCreateLanguageProficiency'
import { useDeleteLanguageProficiency } from '../../../hooks/useDeleteLanguageProficiency'
import { useGetLanguageProficiency } from '../../../hooks/useGetLanguageProficiency'
import { ALLOWED_FILE_TYPES } from '../../../utils/constants'
import { LanguageProficiencyType } from '@backend/agents-ui-api/resources/customization'
import { LanguageProficiencyResource } from '@backend/agents-ui-api/resources/student-document/language-proficiency/types'

type LanguageProficiencyFormFields = {
  hasLanguageProficiency: string
  id: string
  testType: string
  certificateNumber: string
  documents: FileUploadFieldValue[],
  description: string
}

const { Form, Field, useFieldValues, useSetFieldValues } =
  createForm<LanguageProficiencyFormFields>()

type LanguageProficiencyTabProps = {
  disabled?: boolean
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: Error) => void
  currentIntakeAvailable?: boolean
  onIntakeUnavailable?: () => void
  onPreviousTabButton?: () => void
  onNextTabButton?: () => void
  formId: string
}

const UPLOAD_LIMIT = 1

const defaultValue = {
  testType: '',
  certificateNumber: '',
  documents: [],
  description: '',
}

export function LanguageProficiencyTab(props: Readonly<LanguageProficiencyTabProps>) {
  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: props.application.id,
  })
  const { createLanguageProficiencyAsync, isCreatingLanguageProficiency } = useCreateLanguageProficiency();
  const { deleteLanguageProficiencyAsync, isDeletingLanguageProficiency } = useDeleteLanguageProficiency();

  const { languageProficiencyRecordIdFromApplication } = hasNewLanguageProficiencySchema(props.application);
  const { languageProficiencyRecord, isLoadingLanguageProficiency } = useGetLanguageProficiency({
    studentId: props.application.relationships.student?.data?.id!,
    languageProficiencyId: languageProficiencyRecordIdFromApplication!,
  })

  const { cardNumber, lastTimeline: timelineLength } = useGetTimelineItems(props.application, TimelineTabs.LANGUAGE_PROFICIENCY)

  const handleSubmit = useCallback(async (data: LanguageProficiencyFormFields) => {
    if (props.disabled) {
      props.onSuccess()
    } else if (props.onIntakeUnavailable && !props.currentIntakeAvailable) {
      props.onIntakeUnavailable()
    } else {
      const languageProficiency = {
        hasLanguageProficiency: data.hasLanguageProficiency as HasLanguageProficiencyCodes,
        languageProficiencyData: {} as LanguageProficiencyData,
      }

      // form submission scenario 1: user does not provide language proficiency proof
      if (data.hasLanguageProficiency !== LanguageExamAvailability.HAVE) {
        set(languageProficiency, 'languageProficiencyData', null)

        updateApplication(
          {
            attributes: {
              languageProficiency,
            },
          },
          {
            onSuccess: response => {
              props.onSuccess(response)
            },
            onError: props.onError,
          },
        )
      } else {
        languageProficiency.languageProficiencyData[data.id] = {
          languageProficiencyId: data.id,
        } as LanguageProficiencyInformation;
  
        const documentFieldCurrentValue = data.documents.find((document) => document.sectionReference === data.id);
        const shouldReplaceExistingLPRecord = documentFieldCurrentValue?.sectionReference === props.application.attributes?.languageProficiency?.languageProficiencyData?.[data.id]?.languageProficiencyId;
  
        if(documentFieldCurrentValue) {
          if(shouldReplaceExistingLPRecord) {
            // form submission scenario 2: user is replacing an existing language proficiency record and new file is provided
            await deleteLanguageProficiencyAsync({
              studentId: props.application.relationships.student?.data?.id!,
              languageProficiencyId: languageProficiency.languageProficiencyData[data.id]?.languageProficiencyId!,
            })
  
            await createLanguageProficiencyAsync({
              studentId: props.application.relationships.student?.data?.id!,
              data: {
                type: 'language-proficiency',
                attributes: {
                  contentType: documentFieldCurrentValue.file?.type,
                  fileName: documentFieldCurrentValue.file?.name,
                  documentId: data.id,
                  fileSize: documentFieldCurrentValue.file?.size,
                  testType: data.testType as LanguageProficiencyType,
                  birthDate: props.application.attributes?.personalInformation?.basicPersonalInformation?.dateOfBirth!,
                  certificateNumber: data.certificateNumber,
                  givenName: props.application.attributes?.personalInformation?.basicPersonalInformation?.givenName!,
                  familyName: props.application.attributes?.personalInformation?.basicPersonalInformation?.familyName,
                  middleName: props.application.attributes?.personalInformation?.basicPersonalInformation?.middleName,
                  description: data.description,
                },
                file: documentFieldCurrentValue.file,
              },
            })
  
            updateApplication(
              {
                attributes: {
                  languageProficiency,
                },
              },
              {
                onSuccess: response => {
                  props.onSuccess(response)
                },
                onError: props.onError,
              },
            )
          } else {
            // form submission scenario 3: user is submitting language proficiency record and file for the first time
            await createLanguageProficiencyAsync({
              studentId: props.application.relationships.student?.data.id!,
              data: {
                type: 'language-proficiency',
                attributes: {
                  contentType: documentFieldCurrentValue.file?.type,
                  fileName: documentFieldCurrentValue.file?.name,
                  documentId: data.id,
                  fileSize: documentFieldCurrentValue.file?.size,
                  testType: data.testType as LanguageProficiencyType,
                  birthDate: props.application.attributes?.personalInformation?.basicPersonalInformation?.dateOfBirth!,
                  certificateNumber: data.certificateNumber,
                  givenName: props.application.attributes?.personalInformation?.basicPersonalInformation?.givenName!,
                  familyName: props.application.attributes?.personalInformation?.basicPersonalInformation?.familyName,
                  middleName: props.application.attributes?.personalInformation?.basicPersonalInformation?.middleName,
                  description: data.description,
                },
                file: documentFieldCurrentValue.file,
              },
            })
  
            updateApplication(
              {
                attributes: {
                  languageProficiency,
                },
              },
              {
                onSuccess: response => {
                  props.onSuccess(response)
                },
                onError: props.onError,
              },
            )
          }
        } else {
          if(
            languageProficiencyRecord &&
            languageProficiencyRecord.id === data.id &&
            (languageProficiencyRecord.attributes.testType !== data.testType ||
            languageProficiencyRecord.attributes.certificateNumber !== data.certificateNumber ||
            languageProficiencyRecord.attributes.description !== data.description)
  
          ) {
            // form submission scenario 4: user is replacing language proficiency record but wants to keep existing file
            const { 
              attributes: { 
                fileName,
                testType,
                certificateNumber,
                givenName,
                familyName,
                middleName,
                description,
                birthDate,
              },
              id: documentId
            } = languageProficiencyRecord;

            const fileDownloadUrl = await getLanguageProficiencyFile(props.application.relationships.student?.data?.id!, languageProficiencyRecord?.id!);
            const file = await fetch(fileDownloadUrl.data.meta.downloadUrl);
            const fileBlob = await file.blob();
  
            await deleteLanguageProficiencyAsync({
              studentId: props.application.relationships.student?.data?.id!,
              languageProficiencyId: languageProficiencyRecord?.id!,
            })
            await createLanguageProficiencyAsync({
              studentId: props.application.relationships.student?.data.id!,
              data: {
                type: 'language-proficiency',
                attributes: {
                  contentType: fileBlob.type,
                  fileName,
                  documentId,
                  fileSize: fileBlob.size,
                  testType: languageProficiencyRecord.attributes.testType !== data.testType ? data.testType as LanguageProficiencyType : testType, // user input should take precedence over the existing record
                  certificateNumber: languageProficiencyRecord.attributes.certificateNumber !== data.certificateNumber ? data.certificateNumber  : certificateNumber ?? '', // user input should take precedence over the existing record
                  description: languageProficiencyRecord.attributes.description !== data.description ? data.description ?? '' : description ?? '', // user input should take precedence over the existing record
                  givenName: givenName!,
                  familyName: familyName,
                  middleName: middleName,
                  birthDate: birthDate!,
                },
                file: new File([fileBlob], fileName, { type: fileBlob.type }),
              },
            })
    
            updateApplication(
              {
                attributes: {
                  languageProficiency,
                },
              },
              {
                onSuccess: response => {
                  props.onSuccess(response)
                },
                onError: props.onError,
              },
            )
          } else {
            // form submission scenario 5: user went back to form after submission but has not made any changes
            updateApplication(
              {
                attributes: {
                  languageProficiency,
                },
              },
              {
                onSuccess: response => {
                  props.onSuccess(response)
                },
                onError: props.onError,
              },
            )
          }
        }
      }
    }
  }, [props, updateApplication, createLanguageProficiencyAsync, deleteLanguageProficiencyAsync, languageProficiencyRecord])

  if (isLoadingLanguageProficiency) {
    return <Loading />
  }

  return (
    <Form
      id={props.formId}
      defaultValues={setDefaultLanguageProficiency({ application: props.application, languageProficiencyRecord: languageProficiencyRecord })}
      validationMode={'onSubmit'}
      onSubmit={handleSubmit}
    >
      <ApplicationFormCard
        cardNumber={cardNumber}
        totalCards={timelineLength}
        onPreviousTabButton={props.onPreviousTabButton}
        onNextTabButton={props.onNextTabButton}
        icon="🗣️"
        title="Language Proficiency"
        isLoading={isUpdatingApplication || isCreatingLanguageProficiency || isDeletingLanguageProficiency}
        disabled={props.disabled}
      >
        <LanguageProficiencyItems
          disabled={props.disabled}
          application={props.application}
          isLoadingLanguageProficiency={isLoadingLanguageProficiency}
          languageProficiencyRecord={languageProficiencyRecord} />
      </ApplicationFormCard>
    </Form>
  )
}

type LanguageProficiencyItemsProps = {
  disabled?: boolean
  application: StudentApplication
  isLoadingLanguageProficiency: boolean
  languageProficiencyRecord: LanguageProficiencyResource | undefined
}

function LanguageProficiencyItems(props: Readonly<LanguageProficiencyItemsProps>) {
  const [showUploadErrorNotice, setShowUploadErrorNotice] = useState(false)

  const [dialogData, setDialogData] = useState<{
    currentTestValue: string
    isOpen: boolean
    newTestValue: string | null
  }>({
    currentTestValue: '',
    isOpen: false, // is the dialog open or closed
    newTestValue: null,
  })

  const {
    certificateNumber,
    hasLanguageProficiency,
    id,
    testType,
    description,
  } = useFieldValues([
    'certificateNumber',
    'hasLanguageProficiency',
    'id',
    'testType',
    'description',
  ])
  const setFieldValues = useSetFieldValues()

  const testTypeOptions = useGetLanguageProficiencyTypes({
    files: getFilesOfTypeLanguageProficiency(props.application?.attributes?.files as FileData),
  })


  useEffect(() => {
    if(props.languageProficiencyRecord && !id) {
      setFieldValues({
        id: props.languageProficiencyRecord?.id!,
      })
    }
  }, [props.languageProficiencyRecord, id])

  if (!id || !testTypeOptions) {
    return <Loading />
  }

  return (
    <>
      <Flex direction="column" gap={4}>
        <Text>
          Please provide information about the student's language proficiency as per the program
          requirements.
        </Text>
        <Flex direction="column" gap={4}>
          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis="100%">
              <Field
                as={Select}
                label="Will you provide language proficiency proof now?"
                name={`hasLanguageProficiency`}
                appearance="styled"
                disabled={props.disabled}
                required="This field is required"
                onChange={value => {
                  setFieldValues({
                    hasLanguageProficiency: value as string,
                    id: id,
                    ...defaultValue,
                  })
                }}
              >
                <Select.Option value="HAVE" label="Yes, and I can provide this now" />
                <Select.Option value="WILL_HAVE" label="Yes, but I will provide this later" />
                <Select.Option value="DONT_NEED" label="No, I don’t need to provide this" />
              </Field>
            </Flex.Item>
            {hasLanguageProficiency === LanguageExamAvailability.HAVE ? (
              <>
                <Flex.Item basis="100%">
                  <Field
                    as={Select}
                    label="Proof type"
                    name={`testType`}
                    appearance="styled"
                    disabled={props.disabled}
                    required={
                      isFieldRequired({
                        testType,
                        hasLanguageProficiency: hasLanguageProficiency,
                        disabled: props.disabled,
                      })
                        ? 'Proof type is required'
                        : false
                    }
                    onChange={value => {
                      const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)

                      if (fieldsInfo?.fileType && testType.length !== 0) {
                        setDialogData({
                          currentTestValue: testType,
                          isOpen: true,
                          newTestValue: (value as string | undefined) || null,
                        })
                      }
                    }}
                  >
                    {convertObjToListWithSpecificLastItem(testTypeOptions, 'OTHER').map(([value, option]) => (
                      <Select.Option value={value} label={option.label} key={value} />
                    ))}
                  </Field>
                </Flex.Item>
                <SelectedOtherTypeFields disabled={props.disabled} />
                <SelectedRegisteredTypeField
                  disabled={props.disabled}
                  option={testTypeOptions[testType as keyof typeof testTypeOptions]}
                />
                {showUploadErrorNotice ? (
                  <Flex gap={4} direction="column" basis="100%">
                    <Notice intent="negative">
                      Upload failed. Please try again. If the problem persists, check your file format and
                      size.
                      <Notice.CloseButton onClick={() => setShowUploadErrorNotice(false)} />
                    </Notice>
                  </Flex>
                ) : null}
                <UploadField
                  disabled={props.disabled}
                  application={props.application}
                  isLoadingLanguageProficiency={props.isLoadingLanguageProficiency}
                  languageProficiencyRecord={props.languageProficiencyRecord}
                  setShowUploadErrorNotice={setShowUploadErrorNotice} />
              </>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
      <ChangeLanguageTestDialog
        currentTestLabel={
          testTypeOptions[dialogData.currentTestValue as keyof typeof testTypeOptions] ? testTypeOptions[dialogData.currentTestValue as keyof typeof testTypeOptions].label : '' 
        }
        isOpen={dialogData.isOpen}
        newTestLabel={
          dialogData.newTestValue
            ? testTypeOptions[dialogData.newTestValue as keyof typeof testTypeOptions]?.label
            : ''
        }
        onCloseDialog={(isOpen: boolean) => {
          setFieldValues({
            testType: dialogData.currentTestValue,
          })

          setDialogData({
            currentTestValue: '',
            isOpen: isOpen,
            newTestValue: null,
          })
        }}
        onConfirm={() => {
          setFieldValues({
            testType: dialogData.newTestValue || '',
            certificateNumber: dialogData.newTestValue && isRegisteredLanguageProficiencyType(dialogData.newTestValue) ? certificateNumber : '',
            description: dialogData.newTestValue === 'OTHER' ? description : '',
          })

          setDialogData({
            currentTestValue: '',
            isOpen: false,
            newTestValue: null,
          })
        }}
      />
    </>
  )
}

type SelectedOtherTypeFieldsProps = {
  readonly disabled?: boolean
}

function SelectedOtherTypeFields(props: SelectedOtherTypeFieldsProps) {
  const { testType } = useFieldValues(['testType'])
  if (testType !== 'OTHER') {
    return null
  }
  return (
    <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
      <Flex.Item basis={{ xs: '100%' }}>
        <Field
          // @ts-ignore
          as={TextInput}
          label="Please specify other proof type"
          name={`description`}
          disabled={props.disabled}
          helpText="Provide a description with up to 200 characters"
          validate={value => {
            if (value.length > 200) {
              return 'This field has a character limit of 200'
            }
            return true
          }}
          required={'This field is required'}
        />
      </Flex.Item>
    </Flex>
  )
}

type SelectedRegisteredTypeFieldProps = {
  readonly disabled?: boolean
  readonly option?: LanguageProficiencyTypeOption
}

function SelectedRegisteredTypeField(props: Readonly<SelectedRegisteredTypeFieldProps>) {
  const { testType } = useFieldValues(['testType'])
  if (!isRegisteredLanguageProficiencyType(testType)) {
    return null
  }

  if (!props.option) return null
  return (
    <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
      <Flex.Item basis={{ xs: '100%' }}>
        <Field
          // @ts-ignore
          as={TextInput}
          label={props.option.primaryIdFieldName || 'Registration code'}
          name={`certificateNumber`}
          disabled={props.disabled}
          required={'This field is required'}
        />
      </Flex.Item>
    </Flex>
  )
}

type UploadFieldProps = {
  readonly disabled?: boolean
  readonly application: StudentApplication
  readonly isLoadingLanguageProficiency: boolean
  readonly languageProficiencyRecord: LanguageProficiencyResource | undefined
  readonly setShowUploadErrorNotice: (show: boolean) => void
}

function UploadField(props: UploadFieldProps) {
  const [shouldDisplayFileButton, setShouldDisplayFileButton] = useState(true);
  const { id, testType, documents } = useFieldValues(['id', 'testType', 'documents'])
  const setFieldValues = useSetFieldValues()


  return (
    <Field
      as={FileUploadField}
      allowedFileTypes={ALLOWED_FILE_TYPES}
      application={props.application}
      disabled={props.disabled || props.isLoadingLanguageProficiency}
      fileLimit={UPLOAD_LIMIT}
      fileType={getLanguageProficiencyFieldsInfo(testType)?.fileType as DocumentTags}
      label={
        <Text variant={'bodyM'}>
          Add the language proficiency document below, supported file formats: JPG, JPEG, PDF,
          PNG, max number of files: {UPLOAD_LIMIT} <Asterisk />
        </Text>
      }
      name={`documents`}
      onRemove={(id: string) => {
        setFieldValues({ documents: documents.filter(file => file.id !== id) })
        setShouldDisplayFileButton(false);
      }
      }
      section={id}
      showHistory={!!props.disabled}
      validate={value => {
        if (documents.length > UPLOAD_LIMIT) {
          return `This field has a file limit of ${UPLOAD_LIMIT}.`
        }
        if (!(value.length) && !(props.languageProficiencyRecord && shouldDisplayFileButton)) {
          return 'This field is required'
        }
        return true
      }}
      multiple={false}
      hideUploadComponent={props.languageProficiencyRecord && shouldDisplayFileButton}
      presetValue={
        props.languageProficiencyRecord && shouldDisplayFileButton ?
            [
              {
                id: props.languageProficiencyRecord?.id!,
                uploadDate: new Date(props.languageProficiencyRecord?.attributes.uploadedAt!),
                tags: [getLanguageProficiencyFieldsInfo(testType)?.fileType as DocumentTags],
                name: props.languageProficiencyRecord?.attributes.fileName!,
                section: '',
                history: [],
                isBlankDatePlaceholder: () => true,
                onClick: async () => {
                  if (props.languageProficiencyRecord) {
                    const req = await getLanguageProficiencyFile(props.application.relationships.student?.data?.id!, props.languageProficiencyRecord?.id!)
                    window.open(
                      req.data.meta.downloadUrl,
                      `language_proficiency_${props.languageProficiencyRecord.id}_${Date.now()}`,
                    )
                  }
                },
              }
            ] : undefined
      }
      onChange={([file]) => {
        if (file) {
          props.setShowUploadErrorNotice(false)

          if (
            !ALLOWED_FILE_TYPES.find(fileExtension =>
              file.file.name.toLowerCase().endsWith(fileExtension),
            )
          ) {
            file.errorMessage = `Incorrect file format. Only the following file types may be uploaded: ${ALLOWED_FILE_TYPES.join(
              ', ',
            )}`
          }

          return false
        }
      }}
    />
  )
}

function setDefaultLanguageProficiency({
  application,
  languageProficiencyRecord,
}: {
  application: StudentApplication
  languageProficiencyRecord: LanguageProficiencyResource | undefined
}) {
  const languageProficiency = application?.attributes?.languageProficiency;
  let formData = {
    hasLanguageProficiency: languageProficiency?.hasLanguageProficiency || '',
    ...defaultValue,
    id: nanoid(),
  }

  if (
    !languageProficiency?.languageProficiencyData ||
    !Object.keys(languageProficiency.languageProficiencyData).length
  ) {
    return formData
  }

  let id: string;

  const { languageProficiencyRecordIdFromApplication } = hasNewLanguageProficiencySchema(application);

  // handles the case where application contains a id reference to a language proficiency record
  if(languageProficiencyRecord && languageProficiencyRecordIdFromApplication) {
    id = languageProficiency.languageProficiencyData?.[languageProficiencyRecordIdFromApplication]?.languageProficiencyId!;
    
    formData = {
      hasLanguageProficiency: LanguageExamAvailability.HAVE,
      testType: languageProficiencyRecord?.attributes?.testType,
      certificateNumber: languageProficiencyRecord?.attributes?.certificateNumber!,
      documents: [],
      description: languageProficiencyRecord?.attributes?.description || '',
      id: id,
    }

    return formData
  }

  return formData
}

function isFieldRequired(params: {
  testType: string
  hasLanguageProficiency?: string
  disabled?: boolean
}) {
  if (params.disabled || params.hasLanguageProficiency !== LanguageExamAvailability.HAVE) {
    return false
  }

  return true
}

function isRegisteredLanguageProficiencyType(testType: string) {
  return ['IELTS', 'PTE', 'TOEFL', 'DUOLINGO'].includes(testType);
}
